import { getOrderedRoleKeys } from './roles.utils';
import { formatUserRole } from './users.utils';
import type {
  BulkEditableUser,
  BulkGroupImpact,
  BulkGroupSection,
  BulkRoleImpact,
  MembershipsByUser,
} from '../types/bulk-edit-users.types';
import type { GroupWithMemberCount, UserRole } from '../types/database';

export function getEffectiveSelectedUserIds(
  selectedUserIds: Iterable<string>,
  currentUserId?: string
): string[] {
  return Array.from(selectedUserIds).filter(
    (userId) => userId !== currentUserId
  );
}

export function getSelectableUserIds(
  users: BulkEditableUser[],
  currentUserId?: string
): string[] {
  return users
    .filter((user) => user.id !== currentUserId)
    .map((user) => user.id);
}

export function getBulkRoleImpact(
  users: BulkEditableUser[],
  selectedUserIds: string[],
  targetRole: UserRole
): BulkRoleImpact {
  const selectedIdSet = new Set(selectedUserIds);
  const selectedUsers = users.filter((user) => selectedIdSet.has(user.id));
  const willChangeCount = selectedUsers.filter(
    (user) => user.role !== targetRole
  ).length;
  const fromRoles = new Set(
    selectedUsers
      .filter((user) => user.role !== targetRole)
      .map((user) => user.role)
  );

  return {
    selectedCount: selectedUsers.length,
    targetRole,
    willChangeCount,
    alreadyRoleCount: selectedUsers.length - willChangeCount,
    fromRoleLabel:
      fromRoles.size === 1
        ? formatUserRole(Array.from(fromRoles)[0])
        : 'mixed roles',
  };
}

export function getBulkGroupImpact(
  selectedUserIds: string[],
  groups: GroupWithMemberCount[],
  selectedGroupIds: Set<string>,
  membershipsByUser: MembershipsByUser
): BulkGroupImpact {
  const selectedGroups = groups.filter((group) =>
    selectedGroupIds.has(group.id)
  );
  const rows = selectedGroups.map((group) => {
    const alreadyAssignedCount = selectedUserIds.filter((userId) =>
      (membershipsByUser.get(userId) ?? []).some(
        (membership) => membership.group_id === group.id
      )
    ).length;

    return {
      group,
      alreadyAssignedCount,
      newMembershipCount: selectedUserIds.length - alreadyAssignedCount,
    };
  });

  return {
    selectedUserCount: selectedUserIds.length,
    selectedGroups,
    rows,
    newMembershipCount: rows.reduce(
      (total, row) => total + row.newMembershipCount,
      0
    ),
    unchangedMembershipCount: rows.reduce(
      (total, row) => total + row.alreadyAssignedCount,
      0
    ),
  };
}

export function groupBulkGroupsForPicker(
  groups: GroupWithMemberCount[]
): BulkGroupSection[] {
  return getOrderedRoleKeys()
    .map((parentRole) => ({
      parentRole,
      groups: groups.filter((group) => group.parent_role === parentRole),
    }))
    .filter((section) => section.groups.length > 0);
}
